import BigDrawer from "@/components/Drawer/BigDrawer";
import Card from "@/UI/Card";
import Modal from "@/UI/Modal";
import Loader from "@/components/Loader/Loader";
import Tabs, { Tab } from "@/UI/Tabs";
import { loadAllContactPaginated } from "@/redux/rtk/features/CRM/contact/contactSlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";
import { useGetLeadSourcesQuery } from "@/redux/rtk/features/leadSource/leadSourceApi";
import { useGetInterestsQuery } from "@/redux/rtk/features/interest/interestApi";
import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetDegreesQuery } from "@/redux/rtk/features/degree/degreeApi";
import { useGetLeadStatusesQuery } from "@/redux/rtk/features/leadStatus/leadStatusApi";
import {
  useDeleteLeadMutation,
  useUpdateLeadMutation,
} from "@/redux/rtk/features/leads/leadsApi";
import { toastHandler } from "@/utils/functions";
import { NATIONALITIES } from "@/utils/nationalities";
import { COUNTRIES } from "@/utils/countries";
import usePermissions from "@/utils/usePermissions";
import { Button, DatePicker, Form, Input, InputNumber, Select, Mentions, Modal as AntModal } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getSetting } from "@/redux/rtk/features/setting/settingSlice";
import { useNavigate, useParams } from "react-router-dom";
import { FaEdit, FaHistory, FaWhatsapp, FaPaperclip, FaTasks, FaStickyNote } from "react-icons/fa";
import ConvertLead from "./ConvertLead";
import AddLeadSource from "../LeadSource/AddLeadSource";
import AddInterest from "../Interest/AddInterest";
import AddMajor from "../LeadSource/AddMajor";
import AddDegree from "../../CRM/Degree/AddDegree";
import AddLeadStatus from "../LeadStatus/AddLeadStatus";
import AddCampaignCode from "../CampaignCode/AddCampaignCode";
import { useGetCampaignCodesQuery } from "@/redux/rtk/features/campaignCode/campaignCodeApi";
import LeadTimeline from "./LeadTimeline";
import LeadApplications from "./LeadApplications";
import StudentAttachments from "@/components/CRM/StudentAttachments/StudentAttachments";
import LeadTasks from "./LeadTasks";
import { useGetLeadQuery } from "@/redux/rtk/features/leads/leadsApi";

const { Option } = Select;

export default function LeadsProfile({ leads, leadsLoading }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [triggerSave, setTriggerSave] = useState(false);
  const { data: setting } = useSelector((state) => state.setting);
  const [fieldConfig, setFieldConfig] = useState({});

  useEffect(() => {
    if (!setting) {
      dispatch(getSetting());
    }
  }, [dispatch, setting]);

  useEffect(() => {
    if (setting?.leadFieldConfig) {
      try {
        setFieldConfig(JSON.parse(setting.leadFieldConfig));
      } catch (e) {
        console.error("Error parsing lead field config", e);
      }
    }
  }, [setting]);

  const isRequired = (key) => {
    return fieldConfig[key] === undefined ? true : fieldConfig[key];
  };

  // Watch form values for real-time icon updates
  const watchedMobile = Form.useWatch("mobile", form);
  const watchedPhone = Form.useWatch("phone", form);

  // Helper function to get ID from value (handles both ID and object)
  const getId = useCallback((value) => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "object" && value !== null && value.id !== undefined) {
      return value.id;
    }
    if (typeof value === "number") return value;
    if (typeof value === "string" && value !== "") {
      const num = parseInt(value);
      return isNaN(num) ? undefined : num;
    }
    return undefined;
  }, []);

  const normalizeGender = (value) => {
    if (!value) return "";
    const normalized = String(value).toLowerCase();
    if (normalized === "male") return "Male";
    if (normalized === "female") return "Female";
    return value;
  };

  const { list: ownerList, loading: ownerLoading } = useSelector(
    (state) => state.users
  );

  const { data: leadSources, isLoading: leadSourceLoading, refetch: refetchLeadSources } =
    useGetLeadSourcesQuery();
  const { data: interests, isLoading: interestLoading, refetch: refetchInterests } =
    useGetInterestsQuery();
  const { data: majors, isLoading: majorLoading, refetch: refetchMajors } =
    useGetMajorsQuery();
  const { data: degrees, isLoading: degreeLoading, refetch: refetchDegrees } =
    useGetDegreesQuery();
  const { data: leadStatuses, isLoading: leadStatusLoading, refetch: refetchLeadStatuses } =
    useGetLeadStatusesQuery();
  const { data: campaignCodes, isLoading: campaignCodeLoading, refetch: refetchCampaignCodes } =
    useGetCampaignCodesQuery();

  const resolveInterestId = useCallback((value) => {
    if (value === null || value === undefined || value === "") return undefined;
    const id = getId(value);
    if (id) return id;
    const byName = interests?.find((item) => String(item.name).toLowerCase() === String(value).toLowerCase());
    return byName?.id;
  }, [getId, interests]);

  // Refetch lead to get updated attachments
  const { refetch: refetchLead } = useGetLeadQuery(id, { skip: !id });

  const [deleteLead] = useDeleteLeadMutation();

  const onDelete = async () => {
    AntModal.confirm({
      title: t("common.confirm") + " " + t("common.delete") + "?",
      content: t("common.deleteConfirmContent") || t("common.areYouSure", { action: t("common.delete") }),
      okText: t("common.yes"),
      okType: 'danger',
      cancelText: t("common.no"),
      onOk: async () => {
        try {
          const response = await deleteLead({ id, status: false });
          if (response.data) {
            navigate(-1);
            dispatch(loadAllContactPaginated({}));
          }
        } catch (error) {
          toastHandler(t("errors.somethingWentWrong"), "error");
        }
      },
    });
  };

  // contact profile edit form
  const { permissions } = usePermissions();
  const canEdit = permissions?.includes("update-lead");

  const [updateLeads, { isLoading }] = useUpdateLeadMutation();

  const onFinish = async (values) => {
    try {
      const formData = {
        firstName: values.firstName,
        lastName: values.lastName,
        mobile: values.mobile,
        country: values.country,
        email: values.email,
        phone: values.phone,
        leadOwnerId: getId(values.leadOwnerId) || null,
        leadSourceId: getId(values.leadSourceId) || null,
        nationality: values.nationality,
        description: values.description,
        interest: resolveInterestId(values.interest) || null,
        leadStatus: values.leadStatus || null,
        majorId: values.majorId || null,
        adId: values.adId,
        campaignCode: values.campaignCode,
        language_required: values.language_required,
        degree: values.degree,
        country_of_study: values.country_of_study,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format("YYYY-MM-DD") : null,
        gender: values.gender || null,
      };

      const resp = await updateLeads({ id: leads.id, values: formData });
      if (resp.data) {
        setTriggerSave(false);
      }
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const [convert, setConvert] = useState(false);

  const onCancel = () => {
    setTriggerSave(false);
    form.resetFields();
    // Reset to original values
    if (leads) {
      const convertToString = (value) => {
        if (value === undefined || value === null) return "";
        const cleaned = String(value).replace(/[^0-9+]/g, "");
        return cleaned || "";
      };

      form.setFieldsValue({
        firstName: leads?.firstName || "",
        lastName: leads?.lastName || "",
        mobile: convertToString(leads?.mobile),
        country: leads?.country || "",
        email: leads?.email || "",
        phone: convertToString(leads?.phone),
        leadOwnerId: getId(leads?.leadOwnerId) || getId(leads?.leadOwner?.id) || "",
        leadSourceId: getId(leads?.leadSourceId) || getId(leads?.leadSource?.id) || "",
        nationality: leads?.nationality || "",
        description: leads?.description || "",
        interest: resolveInterestId(leads?.interest) || resolveInterestId(leads?.interestName) || getId(leads?.interestRel?.id) || "",
        leadStatus: leads?.leadStatus || getId(leads?.leadStatusRel?.id) || "",
        majorId: leads?.majorId || getId(leads?.majorRel?.id) || "",
        adId: leads?.adId || "",
        campaignCode: leads?.campaignCode || "",
        agentId: getId(leads?.agentId) || getId(leads?.agent?.id) || "",
        language_required: leads?.languageRequired || leads?.language_required || "",
        degree: leads?.degree || "",
        country_of_study: leads?.countryOfStudy || leads?.country_of_study || "",
        dateOfBirth: leads?.dateOfBirth ? dayjs(leads.dateOfBirth) : undefined,
        gender: normalizeGender(leads?.gender),
      });
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.error("Form validation failed:", errorInfo);
    toastHandler(t("form.validationFailed") + " " + t("form.requiredField"), "warning");
  };

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
  }, [dispatch]);

  useEffect(() => {
    if (leads) {
      // Helper function to keep phone/mobile as cleaned string (so Input shows it)
      const convertToString = (value) => {
        if (value === undefined || value === null) return "";
        const cleaned = String(value).replace(/[^0-9+]/g, "");
        return cleaned || "";
      };

      form.setFieldsValue({
        firstName: leads?.firstName || "",
        lastName: leads?.lastName || "",
        mobile: convertToString(leads?.mobile),
        country: leads?.country || "",
        email: leads?.email || "",
        phone: convertToString(leads?.phone),
        leadOwnerId: getId(leads?.leadOwnerId) || getId(leads?.leadOwner?.id) || "",
        leadSourceId: getId(leads?.leadSourceId) || getId(leads?.leadSource?.id) || "",
        nationality: leads?.nationality || "",
        description: leads?.description || "",
        interest: resolveInterestId(leads?.interest) || resolveInterestId(leads?.interestName) || getId(leads?.interestRel?.id) || "",
        leadStatus: leads?.leadStatus || getId(leads?.leadStatusRel?.id) || "",
        majorId: leads?.majorId || getId(leads?.majorRel?.id) || "",
        adId: leads?.adId || "",
        campaignCode: leads?.campaignCode || "",
        agentId: getId(leads?.agentId) || getId(leads?.agent?.id) || "",
        language_required: leads?.languageRequired || leads?.language_required || "",
        degree: leads?.degree || "",
        country_of_study: leads?.countryOfStudy || leads?.country_of_study || "",
        dateOfBirth: leads?.dateOfBirth ? dayjs(leads.dateOfBirth) : undefined,
        gender: normalizeGender(leads?.gender),
      });
    }
  }, [form, leads, getId, resolveInterestId]);

  // Country list
  const countries = COUNTRIES;

  // Nationality list
  const nationalities = NATIONALITIES;

  return (
    <>
      {leads ? (
        <Card className="border-none">
          <div className="w-full">
            <div className="flex justify-between items-center px-5 p-3 border-b">
              <div className="flex items-center gap-5">
                <div className="flex items-center dark:text-white">
                  <span className="font-bold text-xl">
                    {leads?.firstName} {leads?.lastName}
                  </span>
                </div>
                {triggerSave && (
                  <div className="flex items-center gap-4">
                    <Button
                      type="primary"
                      htmlType="submit"
                      form="lead-profile-form"
                      loading={isLoading}
                    >
                      {t("common.save")}
                    </Button>
                    <Button
                      danger
                      className="border-red-500 text-red-500 bg-white hover:bg-red-50"
                      onClick={onCancel}>
                      {t("common.cancel")}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  disabled={leads?.isConverted === "true"}
                  onClick={() => setConvert(true)}
                  className={
                    leads?.isConverted === "true"
                      ? "bg-gray-500 text-white"
                      : "bg-primary text-white"
                  }>
                  {leads?.isConverted === "true" ? t("lead.converted") : t("lead.convert")}
                </Button>
                <Button danger onClick={onDelete}>
                  {t("common.delete")}
                </Button>
              </div>
            </div>

            <div className="p-5">
              <Tabs>
                <Tab label={t("lead.editInformation")} icon={<FaEdit />}>
                  <Form
                    form={form}
                    id="lead-profile-form"
                    colon={false}
                    disabled={!canEdit}
                    layout="vertical"
                    onFieldsChange={() => setTriggerSave(true)}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                  >
                    <div className="pt-4">
                      {/* Basic Information Section */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">{t("lead.basicInformation")}</h3>
                        <div className="w-full grid grid-cols-2 gap-4">
                          <Form.Item
                            label={t("lead.firstName")}
                            name="firstName"
                            rules={[{ required: isRequired("firstName"), message: t("lead.firstNameRequired") || "Required" }]}>
                            <Input placeholder={t("lead.firstName")} />
                          </Form.Item>

                          <Form.Item
                            label={t("lead.lastName")}
                            name="lastName"
                            rules={[{ required: isRequired("lastName"), message: t("lead.lastNameRequired") || "Required" }]}>
                            <Input placeholder={t("lead.lastName")} />
                          </Form.Item>

                          <Form.Item
                            label={t("lead.mobile")}
                            className="mb-0"
                            required={isRequired("mobile")}
                          >
                            <div className="flex items-center gap-2">
                              <Form.Item
                                name="mobile"
                                noStyle
                                normalize={(value) => value.replace(/[^0-9+]/g, "")}
                                rules={[
                                  { required: isRequired("mobile"), message: t("lead.mobileRequired") || "Required" },
                                  { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
                                ]}
                              >
                                <Input
                                  placeholder={t("lead.mobile") || "+1 (273) 642-1785"}
                                  style={{ width: "100%" }}
                                  inputMode="tel"
                                />
                              </Form.Item>
                              {watchedMobile && (
                                <a
                                  href={`https://wa.me/${watchedMobile.toString().replace(/[^0-9+]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                                  title={t("common.openInWhatsapp") || "Open in WhatsApp"}>
                                  <FaWhatsapp className="text-lg" />
                                </a>
                              )}
                            </div>
                          </Form.Item>
                          <Form.Item
                            label={t("lead.dateOfBirth")}
                            name="dateOfBirth"
                            rules={[{ required: isRequired("dateOfBirth"), message: t("common.required") || "Required" }]}>
                            <DatePicker
                              style={{ width: "100%" }}
                              format="DD/MM/YYYY"
                              placeholder="DD/MM/YYYY"
                            />
                          </Form.Item>

                          <Form.Item
                            label={t("lead.country")}
                            name="country"
                            rules={[{ required: isRequired("country"), message: t("common.required") || "Required" }]}>
                            <Select
                              style={{ width: "100%" }}
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              placeholder={t("lead.country")}>
                              {countries.map((item) => (
                                <Option key={item.value} value={item.value}>
                                  {item.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            label={t("lead.country_of_study")}
                            name="country_of_study"
                            rules={[{ required: isRequired("country_of_study"), message: t("common.required") || "Required" }]}>
                            <Select
                              style={{ width: "100%" }}
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              placeholder={t("lead.country_of_study")}>
                              {countries.map((item) => (
                                <Option key={item.value} value={item.value}>
                                  {item.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            label={t("common.email")}
                            name="email"
                            rules={[{ type: "email", message: t("lead.invalidEmail") }, { required: isRequired("email"), message: t("common.required") || "Required" }]}>
                            <Input placeholder={t("common.email")} />
                          </Form.Item>

                          <Form.Item
                            label={t("application.gender")}
                            name="gender"
                            rules={[{ required: isRequired("gender"), message: t("common.required") || "Required" }]}>
                            <Select
                              style={{ width: "100%" }}
                              allowClear
                              placeholder={t("application.selectGender")}>
                              <Option value="Male">{t("application.male")}</Option>
                              <Option value="Female">{t("application.female")}</Option>
                            </Select>
                          </Form.Item>

                          <Form.Item
                            label={t("lead.phoneNumber")}
                            className="mb-0"
                            required={isRequired("phone")}
                          >
                            <div className="flex items-center gap-2">
                              <Form.Item
                                name="phone"
                                noStyle
                                normalize={(value) => value.replace(/[^0-9+]/g, "")}
                                rules={[
                                  { required: isRequired("phone"), message: t("common.required") || "Required" },
                                  { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
                                ]}
                              >
                                <Input
                                  placeholder={t("lead.phoneNumber") || "+1 (273) 642-1785"}
                                  style={{ width: "100%" }}
                                  inputMode="tel"
                                />
                              </Form.Item>
                              {watchedPhone && (
                                <a
                                  href={`https://wa.me/${watchedPhone.toString().replace(/[^0-9+]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center min-w-[40px] h-10 rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors"
                                  title={t("common.openInWhatsapp") || "Open in WhatsApp"}>
                                  <FaWhatsapp className="text-lg" />
                                </a>
                              )}
                            </div>
                          </Form.Item>
                        </div>
                      </div>

                      {/* Lead Information Section */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">{t("lead.leadInformation")}</h3>
                        <div className="w-full grid grid-cols-2 gap-4">
                          <Form.Item
                            label={t("lead.leadOwner")}
                            name={"leadOwnerId"}
                            rules={[{ required: isRequired("leadOwnerId"), message: t("lead.selectLeadOwner") || "Required" }]}>
                            <Select
                              key={ownerLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={ownerLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectLeadOwner")}
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={(() => {
                                const currentList = Array.isArray(ownerList) ? [...ownerList] : [];
                                const cId = getId(form.getFieldValue("leadOwnerId")) || getId(leads?.leadOwnerId) || getId(leads?.leadOwner?.id);
                                const currentUserId = localStorage.getItem("id");
                                const currentUserName = localStorage.getItem("user");

                                let cName = leads?.leadOwnerUsername;
                                if (!cName && leads?.leadOwner) {
                                  if (typeof leads.leadOwner === 'object') {
                                    cName = leads.leadOwner.username || `${leads.leadOwner.firstName || ''} ${leads.leadOwner.lastName || ''}`.trim();
                                  } else {
                                    cName = leads.leadOwner;
                                  }
                                }

                                // If the owner is the current user and we don't have a name from the lead, use the name from localStorage
                                if (getId(cId) === getId(currentUserId) && !cName) {
                                  cName = currentUserName;
                                }

                                if (cId && !currentList.some(m => getId(m.id) === cId)) {
                                  currentList.push({ id: cId, username: cName || `User ${cId}` });
                                }
                                return currentList.map((item) => ({
                                  value: getId(item.id),
                                  label: item.username,
                                }));
                              })()}
                            />
                          </Form.Item>

                          <Form.Item
                            label={
                              <>
                                {t("lead.leadSource")}
                                <BigDrawer title={t("lead.leadSource")} onClose={refetchLeadSources}>
                                  <AddLeadSource drawer={true} onClose={refetchLeadSources} />
                                </BigDrawer>
                              </>
                            }
                            name={"leadSourceId"}
                            rules={[{ required: isRequired("leadSourceId"), message: t("lead.leadSourceRequired") || "Required" }]}>
                            <Select
                              key={leadSourceLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={leadSourceLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectLeadSource")}
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={(() => {
                                const currentList = Array.isArray(leadSources) ? [...leadSources] : [];
                                const cId = getId(form.getFieldValue("leadSourceId")) || getId(leads?.leadSourceId) || getId(leads?.leadSource?.id);
                                const cName = leads?.sourceName || leads?.leadSource?.name;

                                if (cId && !currentList.some(m => getId(m.id) === cId)) {
                                  currentList.push({ id: cId, name: cName || `Source ${cId}` });
                                }
                                return currentList.map((item) => ({
                                  value: getId(item.id),
                                  label: item.name,
                                }));
                              })()}
                            />
                          </Form.Item>

                          <Form.Item
                            label={t("lead.nationality")}
                            name="nationality"
                            rules={[{ required: isRequired("nationality"), message: t("common.required") || "Required" }]}>
                            <Select
                              style={{ width: "100%" }}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectNationality")}
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={nationalities}
                            />
                          </Form.Item>






                          <Form.Item
                            label={
                              <>
                                {t("lead.interest")}
                                <BigDrawer title={t("lead.interest")} onClose={refetchInterests}>
                                  <AddInterest drawer={true} onClose={refetchInterests} />
                                </BigDrawer>
                              </>
                            }
                            name="interest"
                            rules={[{ required: isRequired("interest"), message: t("lead.interestRequired") || "Required" }]}>
                            <Select
                              key={interestLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={interestLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectInterest")}
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={(() => {
                                const currentList = Array.isArray(interests) ? [...interests] : [];
                                const rawInterest = form.getFieldValue("interest") || leads?.interest;
                                const cId = resolveInterestId(rawInterest) || resolveInterestId(leads?.interestName) || getId(leads?.interestRel?.id);
                                const cName = leads?.interestName || leads?.interestRel?.name || (cId ? undefined : rawInterest);

                                if (cId && !currentList.some(m => getId(m.id) === cId)) {
                                  currentList.push({ id: cId, name: cName || `Interest ${cId}` });
                                } else if (!cId && cName && !currentList.some(m => String(m.name).toLowerCase() === String(cName).toLowerCase())) {
                                  currentList.push({ id: cName, name: cName });
                                }
                                return currentList.map((item) => ({
                                  value: getId(item.id) || item.id,
                                  label: item.name,
                                }));
                              })()}
                            />
                          </Form.Item>

                          <Form.Item
                            label={
                              <>
                                {t("lead.leadStatus")}
                                <BigDrawer title={t("lead.leadStatus")} onClose={refetchLeadStatuses}>
                                  <AddLeadStatus drawer={true} onClose={refetchLeadStatuses} />
                                </BigDrawer>
                              </>
                            }
                            name={"leadStatus"}
                            rules={[{ required: isRequired("leadStatus"), message: t("common.required") || "Required" }]}>
                            <Select
                              key={leadStatusLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={leadStatusLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectLeadStatus")}
                              optionLabelProp="label"
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.labelString ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={(() => {
                                const currentList = Array.isArray(leadStatuses) ? [...leadStatuses] : [];
                                const cId = getId(form.getFieldValue("leadStatus")) || getId(leads?.leadStatus) || getId(leads?.leadStatusRel?.id);
                                const cName = leads?.statusName || leads?.leadStatusRel?.name;

                                if (cId && !currentList.some(m => getId(m.id) === cId)) {
                                  currentList.push({ id: cId, name: cName || `Status ${cId}`, color: "#ccc" });
                                }
                                return currentList.map((item) => ({
                                  value: getId(item.id),
                                  label: (
                                    <div className="flex items-center gap-2">
                                      <span
                                        style={{
                                          display: "inline-block",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: item.color,
                                          borderRadius: "2px",
                                        }}
                                      />
                                      {item.name}
                                    </div>
                                  ),
                                  labelString: item.name || "",
                                }));
                              })()}
                            />
                          </Form.Item>

                          <Form.Item
                            label={
                              <>
                                {t("lead.major")}

                              </>
                            }
                            name="majorId"
                            rules={[{ required: isRequired("majorId"), message: t("lead.majorRequired") || "Required" }]}>
                            <Select
                              key={majorLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={majorLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectMajor")}
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={(() => {
                                const currentList = Array.isArray(majors) ? [...majors] : [];
                                const cId = getId(form.getFieldValue("majorId")) || getId(leads?.majorId) || getId(leads?.majorRel?.id);
                                const cName = leads?.majorName || leads?.majorRel?.name;

                                if (cId && !currentList.some(m => getId(m.id) === cId)) {
                                  currentList.push({ id: cId, name: cName || `Major ${cId}` });
                                }
                                return currentList.map((item) => ({
                                  value: getId(item.id),
                                  label: item.name,
                                }));
                              })()}
                            />
                          </Form.Item>
                        </div>
                      </div>

                      {/* Additional Information Section */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">{t("lead.additionalInformation")}</h3>
                        <div className="w-full grid grid-cols-2 gap-4">
                          <Form.Item label={t("lead.adId")} name="adId">
                            <Input placeholder={t("lead.enterAdId")} />
                          </Form.Item>

                          <Form.Item
                            label={
                              <>
                                {t("lead.campaignCode")}
                                <BigDrawer title={t("campaignCode.createCampaignCode") || "Create Campaign Code"} onClose={refetchCampaignCodes}>
                                  <AddCampaignCode drawer={true} onClose={refetchCampaignCodes} />
                                </BigDrawer>
                              </>
                            }
                            name="campaignCode"
                            rules={[{ required: isRequired("campaignCode"), message: t("lead.campaignCodeRequired") || "Required" }]}
                          >
                            <Select
                              key={campaignCodeLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={campaignCodeLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectCampaignCode") || "Select campaign code"}
                              options={(() => {
                                const currentList = Array.isArray(campaignCodes) ? [...campaignCodes] : [];
                                const cName = form.getFieldValue("campaignCode") || leads?.campaignCode;

                                if (cName && !currentList.some(m => m.name === cName)) {
                                  currentList.push({ id: cName, name: cName });
                                }
                                return currentList.map((item) => ({
                                  value: item.name,
                                  label: item.name,
                                }));
                              })()}
                            />
                          </Form.Item>


                          <Form.Item
                            label={
                              <>
                                {t("lead.degree")}
                                <BigDrawer title={t("lead.degree")} onClose={refetchDegrees}>
                                  <AddDegree onClose={refetchDegrees} />
                                </BigDrawer>
                              </>
                            }
                            name="degree"
                            rules={[{ required: isRequired("degree"), message: t("common.required") || "Required" }]}>
                            <Select
                              key={degreeLoading ? "loading" : "ready"}
                              style={{ width: "100%" }}
                              loading={degreeLoading}
                              allowClear
                              showSearch
                              placeholder={t("lead.selectDegree")}
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={(() => {
                                const currentList = Array.isArray(degrees) ? [...degrees] : [];
                                const cName = leads?.degree;

                                if (cName && !currentList.some(m => m.name === cName)) {
                                  currentList.push({ id: cName, name: cName });
                                }
                                return currentList.map((item) => ({
                                  value: item.name,
                                  label: item.name,
                                }));
                              })()}
                            />
                          </Form.Item>
                          <Form.Item
                            label={t("lead.requiredLanguage")}
                            name="language_required"
                            rules={[{ required: isRequired("language_required"), message: t("common.required") || "Required" }]}>
                            <Select
                              style={{ width: "100%" }}
                              allowClear
                              placeholder={t("lead.selectLanguage")}>
                              <Option value="en">{t("lead.languages.en")}</Option>
                              <Option value="tr">{t("lead.languages.tr")}</Option>
                              <Option value="ar">{t("lead.languages.ar")}</Option>
                              <Option value="fr">{t("lead.languages.fr")}</Option>
                              <Option value="ru">{t("lead.languages.ru")}</Option>
                            </Select>
                          </Form.Item>
                        </div>
                      </div>

                      {/* Description Section */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">{t("lead.description")}</h3>
                        <Form.Item
                          name="description"
                          rules={[{ required: isRequired("description"), message: t("common.required") || "Required" }]}>
                          <Mentions
                            rows={8}
                            placeholder={t("lead.notePlaceholder")}
                            className="w-full text-base"
                            autoSize={{ minRows: 8, maxRows: 15 }}
                          >
                            {ownerList?.map((item) => (
                              <Mentions.Option key={item.id} value={item.username}>
                                {item.username}
                              </Mentions.Option>
                            ))}
                          </Mentions>
                        </Form.Item>
                      </div>
                    </div>
                  </Form>
                </Tab>

                <Tab label={t("lead.timelineTab")} icon={<FaHistory />}>
                  <div className="pt-4">
                    <LeadTimeline lead={leads} />
                  </div>
                </Tab>

                <Tab label={t("lead.attachments")} icon={<FaPaperclip />}>
                  <div className="pt-4">
                    <StudentAttachments
                      student={leads}
                      onUpdate={refetchLead}
                      studentType="lead"
                      studentId={leads?.id}
                    />
                  </div>
                </Tab>

                <Tab label={t("task.title")} icon={<FaTasks />}>
                  <LeadTasks leadId={leads?.id} />
                </Tab>
              </Tabs>
            </div>
          </div>
          <Modal
            open={convert}
            onClose={() => setConvert(false)}
            title={t("lead.convertLeadToContact")}>
            <ConvertLead data={leadSources} onClose={() => setConvert(false)} />
          </Modal>
        </Card>
      ) : (
        <Loader />
      )}
    </>
  );
}
